import type { Dayjs } from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { toEnumFromString } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { isSameData, isSameList } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Currency } from '../currency.js';
import { LocalizedString } from '../localized-string.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { GiftCardVariation } from './gift-card-variation.js';
import { GiftCardValidity } from './gift-card-validity.js';

export enum GiftCardTemplateStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export interface GiftCardTemplateCreateArgs {
  uniqueId: string,
  businessRef: BusinessModuleRef,
  creationDate: Dayjs,
  name: LocalizedString,
  description: LocalizedRichDescription | null,
  currency: Currency,
  variations: readonly GiftCardVariation[],
  validity: GiftCardValidity,
  status?: GiftCardTemplateStatus,
}

export class GiftCardTemplate extends DataModel {
  static readonly TYPE_ID: string = 'beb5fee525f647e993fe8dce4b26f10e';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: Dayjs,
    public readonly name: LocalizedString,
    public readonly description: LocalizedRichDescription | null,
    public readonly currency: Currency,
    public readonly variations: GiftCardVariation[],
    public readonly validity: GiftCardValidity,
    public readonly status: GiftCardTemplateStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCardTemplate.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCardTemplate.SCHEMA_VERSION);
  }

  get isGlobal(): boolean {
    return this.businessRef === null;
  }

  static create(args: GiftCardTemplateCreateArgs): GiftCardTemplate {
    return new GiftCardTemplate(
        args.uniqueId,
        args.businessRef.clone(),
        args.creationDate.clone(),
        args.name.clone(),
        args.description?.clone() ?? null,
        args.currency,
        args.variations.map((variation) => variation.clone()),
        args.validity.clone(),
        args.status ?? GiftCardTemplateStatus.Live,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardTemplate {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      name: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'name')),
      description: context.fromDataOrNull<LocalizedRichDescription>(MapUtils.getRecordOrNull(map, 'description')),
      currency: toEnumFromString(MapUtils.getString(map, 'currency'), Currency),
      variations: MapUtils.getList(map, 'variations', (value) => context.fromData<GiftCardVariation>(JSONUtils.decodeRecord(value))),
      validity: context.fromData<GiftCardValidity>(MapUtils.getRecord(map, 'validity')),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GiftCardTemplateStatus),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'name': context.toData(this.name),
      'description': context.toDataOrNull(this.description),
      'currency': this.currency,
      'variations': this.variations.map((variation) => context.toData(variation)),
      'validity': context.toData(this.validity),
      '$status': this.status,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith(data: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    creationDate?: Dayjs,
    name?: LocalizedString,
    description?: LocalizedRichDescription | null,
    currency?: Currency,
    variations?: readonly GiftCardVariation[],
    validity?: GiftCardValidity,
    status?: GiftCardTemplateStatus,
  } = {}): this {
    return GiftCardTemplate.create({
      uniqueId: data.uniqueId ?? this.uniqueId,
      businessRef: data.businessRef ?? this.businessRef,
      creationDate: data.creationDate ?? this.creationDate,
      name: data.name ?? this.name,
      description: data.description === undefined ? this.description : data.description,
      currency: data.currency ?? this.currency,
      variations: data.variations ?? this.variations,
      validity: data.validity ?? this.validity,
      status: data.status ?? this.status,
    }) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      isSameData(this.businessRef, other.businessRef) &&
      this.creationDate.valueOf() === other.creationDate.valueOf() &&
      isSameData(this.name, other.name) &&
      isSameData(this.description, other.description) &&
      this.currency === other.currency &&
      isSameList(this.variations, other.variations, isSameData) &&
      isSameData(this.validity, other.validity) &&
      this.status === other.status;
  }

  findVariationById(uniqueId: string): GiftCardVariation | null {
    for(let i = 0; i < this.variations.length; i++) {
      if(this.variations[i].uniqueId === uniqueId) {
        return this.variations[i];
      }
    }

    return null;
  }
}

export class GiftCardTemplateBuilder extends DataModelBuilder<GiftCardTemplate> {
  build(context: SerializationContext, map: SerializedData): GiftCardTemplate {
    return GiftCardTemplate.fromMap(context, map);
  }
}
